import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { IconTrash } from '../components/icons';

export interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface ConfirmRequest extends Required<ConfirmOptions> {
  message: string;
}

// The context value is the `confirm()` function itself (matching the
// source app's shape exactly) rather than an object wrapping it — callers
// do `const confirmDelete = useConfirm()` then `await confirmDelete('...')`.
type ConfirmFn = (message: string, options?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  const confirm = useCallback<ConfirmFn>((message, options = {}) => {
    // Capture the trigger element now, before the dialog mounts and moves
    // focus via autoFocus — by the time an effect runs, focus has already
    // shifted to Cancel, so this must happen synchronously here.
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
      setRequest({
        message,
        title: options.title ?? 'Are you sure?',
        confirmLabel: options.confirmLabel ?? 'Delete',
        cancelLabel: options.cancelLabel ?? 'Cancel',
      });
    });
  }, []);

  const settle = useCallback((result: boolean) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setRequest(null);
  }, []);

  // Restore focus to whatever triggered the dialog once it closes.
  useEffect(() => {
    if (!request) return;
    return () => previousFocusRef.current?.focus?.();
  }, [request]);

  useEffect(() => {
    if (!request) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        settle(false);
        return;
      }
      if (e.key !== 'Tab') return;
      // Trap Tab/Shift+Tab between the two buttons so focus can't escape
      // the dialog into the page behind it.
      const focusable = [cancelRef.current, confirmRef.current].filter(
        (el): el is HTMLButtonElement => el !== null
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [request, settle]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {request && (
        <div className="modal-backdrop" onClick={() => settle(false)}>
          <div
            className="modal confirm-modal"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={titleId}
          >
            <div className="confirm-icon">
              <IconTrash />
            </div>
            <h2 id={titleId}>{request.title}</h2>
            <p className="confirm-message">{request.message}</p>
            <div className="form-actions confirm-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => settle(false)}
                ref={cancelRef}
                autoFocus
              >
                {request.cancelLabel}
              </button>
              <button type="button" className="btn btn-danger" onClick={() => settle(true)} ref={confirmRef}>
                {request.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
}
